import { create } from "zustand";
import type { CelestialObject, PlanetData, StarSystem, ViewMode, ViewState } from "../types";

// --- Scale limits per view mode ---
const GALAXY_MIN_SCALE = 0.3;
const GALAXY_MAX_SCALE = 10;
const SYSTEM_MIN_SCALE = 1;
const SYSTEM_MAX_SCALE = 4;
const SYSTEM_PAN_LIMIT = 200;

const INITIAL_GALAXY_VIEW: ViewState = {
  offsetX: typeof window !== "undefined" ? window.innerWidth / 2 : 640,
  offsetY: typeof window !== "undefined" ? window.innerHeight / 2 : 480,
  scale: 1,
};

const INITIAL_SYSTEM_VIEW: ViewState = {
  offsetX: typeof window !== "undefined" ? window.innerWidth / 2 : 640,
  offsetY: typeof window !== "undefined" ? window.innerHeight / 2 : 480,
  scale: 3,
};

/** Clamp system view offsets to ±limit from screen center, scaled by zoom */
function clampSystemOffset(view: ViewState): void {
	const cx = typeof window !== "undefined" ? window.innerWidth / 2 : 640;
	const cy = typeof window !== "undefined" ? window.innerHeight / 2 : 480;
	const limit = SYSTEM_PAN_LIMIT * view.scale;
	view.offsetX = Math.max(cx - limit, Math.min(cx + limit, view.offsetX));
	view.offsetY = Math.max(cy - limit, Math.min(cy + limit, view.offsetY));
}

// --- State interface ---

interface SpaceState {
  systems: StarSystem[];
  selectedObject: CelestialObject | null;
  hoveredObject: CelestialObject | null;

  // View mode
  viewMode: ViewMode;
  focusedSystem: StarSystem | null;
  galaxySavedView: ViewState | null;
  galaxyView: ViewState;
  systemView: ViewState;

  // Mission launch
  farmingTarget: { planet: PlanetData; system: StarSystem } | null;

  // Entry animation
  hasEnteredGalaxy: boolean;
  setHasEnteredGalaxy: (val: boolean) => void;

  // UI
  activeTab: "explore" | "shipyard" | "missions";
  searchQuery: string;
  isSearchOpen: boolean;
  isSidebarOpen: boolean;
  isLoading: boolean;
  error: string | null;

  // Data actions
  setSystems: (systems: StarSystem[]) => void;
  selectObject: (obj: CelestialObject | null) => void;
  hoverObject: (obj: CelestialObject | null) => void;

  // View actions (operate on active mode)
  setView: (view: Partial<ViewState>) => void;
  pan: (dx: number, dy: number) => void;
  zoom: (factor: number, centerX?: number, centerY?: number) => void;
  resetView: () => void;

  // Mode transitions
  enterSystemView: (system: StarSystem) => void;
  exitSystemView: () => void;

  // Mission actions
  startMission: (planet: PlanetData, system: StarSystem) => void;
  closeMission: () => void;

  // UI actions
  setActiveTab: (tab: "explore" | "shipyard" | "missions") => void;
  setSearchQuery: (query: string) => void;
  setSearchOpen: (open: boolean) => void;
  toggleSearch: () => void;
  toggleSidebar: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

// --- Store ---

export const useSpaceStore = create<SpaceState>((set, get) => ({
  systems: [],
  selectedObject: null,
  hoveredObject: null,

  viewMode: "galaxy",
  focusedSystem: null,
  galaxySavedView: null,
  galaxyView: INITIAL_GALAXY_VIEW,
  systemView: INITIAL_SYSTEM_VIEW,

  farmingTarget: null,

  hasEnteredGalaxy: false,
  setHasEnteredGalaxy: (val) => set({ hasEnteredGalaxy: val }),

  activeTab: "explore",
  searchQuery: "",
  isSearchOpen: false,
  isSidebarOpen: false,
  isLoading: false,
  error: null,

  setSystems: (systems) => set({ systems }),

  selectObject: (obj) => set({ selectedObject: obj, isSidebarOpen: obj !== null }),

  hoverObject: (obj) => set({ hoveredObject: obj }),

  setView: (partial) =>
    set((state) => {
      const key = state.viewMode === "galaxy" ? "galaxyView" : "systemView";
      const merged = { ...state[key], ...partial };
      if (state.viewMode === "system") clampSystemOffset(merged);
      return { [key]: merged };
    }),

  pan: (dx, dy) =>
    set((state) => {
      const key = state.viewMode === "galaxy" ? "galaxyView" : "systemView";
      const view = {
        ...state[key],
        offsetX: state[key].offsetX + dx,
        offsetY: state[key].offsetY + dy,
      };
      if (state.viewMode === "system") clampSystemOffset(view);
      return { [key]: view };
    }),

  zoom: (factor, centerX, centerY) => {
    const state = get();
    const key = state.viewMode === "galaxy" ? "galaxyView" : "systemView";
    const current = state[key];
    const [minS, maxS] =
      state.viewMode === "galaxy"
        ? [GALAXY_MIN_SCALE, GALAXY_MAX_SCALE]
        : [SYSTEM_MIN_SCALE, SYSTEM_MAX_SCALE];
    const newScale = Math.max(minS, Math.min(maxS, current.scale * factor));

    if (centerX !== undefined && centerY !== undefined) {
      const scaleRatio = newScale / current.scale;
      const view = {
        offsetX: centerX - (centerX - current.offsetX) * scaleRatio,
        offsetY: centerY - (centerY - current.offsetY) * scaleRatio,
        scale: newScale,
      };
      if (state.viewMode === "system") clampSystemOffset(view);
      set({ [key]: view });
    } else {
      set({ [key]: { ...current, scale: newScale } });
    }
  },

  resetView: () =>
    set((state) => {
      if (state.viewMode === "galaxy") return { galaxyView: INITIAL_GALAXY_VIEW };
      return { systemView: INITIAL_SYSTEM_VIEW };
    }),

  enterSystemView: (system) =>
    set((state) => ({
      viewMode: "system" as const,
      focusedSystem: system,
      galaxySavedView: { ...state.galaxyView },
      systemView: {
        offsetX: typeof window !== "undefined" ? window.innerWidth / 2 : 640,
        offsetY: typeof window !== "undefined" ? window.innerHeight / 2 : 480,
        scale: 3,
      },
      selectedObject: null,
      isSidebarOpen: false,
    })),

  exitSystemView: () =>
    set((state) => ({
      viewMode: "galaxy" as const,
      focusedSystem: null,
      galaxyView: state.galaxySavedView ?? INITIAL_GALAXY_VIEW,
      galaxySavedView: null,
      selectedObject: null,
      isSidebarOpen: false,
    })),

  startMission: (planet, system) =>
    set({ farmingTarget: { planet, system }, isSidebarOpen: false, selectedObject: null }),

  closeMission: () => set({ farmingTarget: null }),

  setActiveTab: (tab) => set({ activeTab: tab }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setSearchOpen: (open) => set({ isSearchOpen: open }),
  toggleSearch: () => set((state) => ({ isSearchOpen: !state.isSearchOpen })),
  toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
}));

// --- Selectors ---

export const useSelectedObject = () => useSpaceStore((s) => s.selectedObject);
export const useHoveredObject = () => useSpaceStore((s) => s.hoveredObject);
export const useViewMode = () => useSpaceStore((s) => s.viewMode);
export const useFocusedSystem = () => useSpaceStore((s) => s.focusedSystem);
export const useActiveView = () =>
  useSpaceStore((s) => (s.viewMode === "galaxy" ? s.galaxyView : s.systemView));
export const useSystems = () => useSpaceStore((s) => s.systems);
export const useFarmingTarget = () => useSpaceStore((s) => s.farmingTarget);
export const useActiveTab = () => useSpaceStore((s) => s.activeTab);
